<template>
  <div class="min-h-screen bg-gray-100">
    <!-- SEO Content -->
    <div class="hidden">
      <h1>Mapa de posicionamiento de cámaras - VisionHub</h1>
      <p>
        Herramienta profesional para la planificación y optimización de sistemas de videovigilancia.
        Diseña y visualiza la cobertura de cámaras de seguridad en tiempo real.
        Solución líder en España para empresas de seguridad y profesionales del sector.
      </p>
    </div>

    <div class="max-w-7xl mx-auto px-4 py-8">
      <div class="flex items-center space-x-4 mb-8">
        <Camera class="w-8 h-8 text-[#18223B]" />
        <h1 class="text-3xl font-bold text-[#18223B] font-poppins">
          Mapa de posicionamiento de cámaras
        </h1>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div class="lg:col-span-2 space-y-6">
          <CameraControls
            v-model:settings="settings"
            :isAddingCamera="isAddingCamera"
            @add-camera="startAddingCamera"
            @reset="resetAll"
          />
          
          <CameraMap
            :cameras="cameras"
            :settings="settings"
            :isAddingCamera="isAddingCamera"
            @camera-added="handleCameraAdded"
            @camera-moved="handleCameraMoved"
            @target-moved="handleTargetMoved"
          />

          <ElevationProfile
            :camera="activeCamera"
            :settings="settings"
            :key="activeCamera?.id || 'no-camera'"
          />
        </div>

        <div class="space-y-6">
          <CameraList
            :cameras="cameras"
            :activeCamera="activeCamera"
            @select="selectCamera"
            @delete="deleteCamera"
            @toggle="toggleCamera"
            @update="updateCamera"
          />

          <CTAPanel @contact="handleContact" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { Camera } from 'lucide-vue-next';
import CameraControls from './components/CameraControls.vue';
import CameraMap from './components/CameraMap.vue';
import CameraList from './components/CameraList.vue';
import ElevationProfile from './components/ElevationProfile.vue';
import CTAPanel from './components/CTAPanel.vue';
import type { Camera as CameraType, CameraSettings } from './types';

const cameras = ref<CameraType[]>([]);
const activeCamera = ref<CameraType | null>(null);
const isAddingCamera = ref(false);

const settings = ref<CameraSettings>({
  height: 6,
  tilt: 0,
  fov: 110,
  targetHeight: 6,
});

// Persistencia local
watch(cameras, (newCameras) => {
  localStorage.setItem('cameras', JSON.stringify(newCameras));
}, { deep: true });

// Cargar cámaras guardadas
const savedCameras = localStorage.getItem('cameras');
if (savedCameras) {
  cameras.value = JSON.parse(savedCameras);
}

const startAddingCamera = () => {
  isAddingCamera.value = true;
};

const handleCameraAdded = (camera: CameraType) => {
  cameras.value.push(camera);
  activeCamera.value = camera;
  isAddingCamera.value = false;
};

const handleCameraMoved = (id: string, position: [number, number]) => {
  const camera = cameras.value.find(c => c.id === id);
  if (camera) {
    camera.position = position;
    if (activeCamera.value?.id === id) {
      activeCamera.value = { ...camera };
    }
  }
};

const handleTargetMoved = (id: string, target: [number, number]) => {
  const camera = cameras.value.find(c => c.id === id);
  if (camera) {
    camera.target = target;
    if (activeCamera.value?.id === id) {
      activeCamera.value = { ...camera };
    }
  }
};

const selectCamera = (camera: CameraType) => {
  activeCamera.value = activeCamera.value?.id === camera.id ? null : { ...camera };
};

const deleteCamera = (id: string) => {
  const index = cameras.value.findIndex(c => c.id === id);
  if (index !== -1) {
    cameras.value.splice(index, 1);
    if (activeCamera.value?.id === id) {
      activeCamera.value = null;
    }
  }
};

const toggleCamera = (id: string) => {
  const camera = cameras.value.find(c => c.id === id);
  if (camera) {
    camera.isActive = !camera.isActive;
    if (activeCamera.value?.id === id) {
      activeCamera.value = { ...camera };
    }
  }
};

const updateCamera = (camera: CameraType) => {
  const index = cameras.value.findIndex(c => c.id === camera.id);
  if (index !== -1) {
    cameras.value[index] = { ...camera };
    if (activeCamera.value?.id === camera.id) {
      activeCamera.value = { ...camera };
    }
  }
};

const resetAll = () => {
  cameras.value = [];
  activeCamera.value = null;
  isAddingCamera.value = false;
};

const handleContact = () => {
  const selectedCameras = cameras.value.filter(camera => camera.isActive).map(camera => ({
    id: camera.id,
    position: camera.position,
    target: camera.target,
    height: camera.height,
    tilt: camera.tilt,
    fov: camera.fov
  }));
  
  console.log('Cameras data ready for webhook:', selectedCameras);
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

.font-poppins {
  font-family: 'Poppins', sans-serif;
}
</style>